import { db } from '../config/firebase';
import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  onSnapshot,
  serverTimestamp,
  writeBatch,
  updateDoc
} from 'firebase/firestore';

class DataSyncService {
  constructor() {
    this.syncInProgress = false;
    this.syncQueue = [];
  }

  // ===== USER DATA SYNC =====
  
  // Sync user profile data
  async syncUserProfile(userId, profileData) {
    try {
      const userRef = doc(db, 'user_profiles', userId);
      await setDoc(userRef, {
        ...profileData,
        userId,
        lastSynced: serverTimestamp(),
        updatedAt: serverTimestamp()
      }, { merge: true });
      
      return { success: true };
    } catch (error) {
      console.error('Error syncing user profile:', error);
      return { success: false, error: error.message };
    }
  }

  // Get user profile from database
  async getUserProfile(userId) {
    try {
      const userRef = doc(db, 'user_profiles', userId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        return { success: true, data: userDoc.data() };
      } else {
        return { success: false, error: 'Profile not found' };
      }
    } catch (error) {
      console.error('Error getting user profile:', error);
      return { success: false, error: error.message };
    }
  }

  // ===== LAWYER PROFILE SYNC =====
  
  // Sync lawyer profile data
  async syncLawyerProfile(lawyerId, profileData) {
    try {
      const lawyerRef = doc(db, 'lawyer_profiles', lawyerId);
      await setDoc(lawyerRef, {
        ...profileData,
        lawyerId,
        lastSynced: serverTimestamp(),
        updatedAt: serverTimestamp()
      }, { merge: true });
      
      return { success: true };
    } catch (error) {
      console.error('Error syncing lawyer profile:', error);
      return { success: false, error: error.message };
    }
  }

  // Get lawyer profile from database
  async getLawyerProfile(lawyerId) {
    try {
      const lawyerRef = doc(db, 'lawyer_profiles', lawyerId);
      const lawyerDoc = await getDoc(lawyerRef);
      
      if (lawyerDoc.exists()) {
        return { success: true, data: lawyerDoc.data() };
      } else {
        return { success: false, error: 'Profile not found' };
      }
    } catch (error) {
      console.error('Error getting lawyer profile:', error);
      return { success: false, error: error.message };
    }
  }

  // ===== CHAT DATA SYNC =====
  
  // Sync chat data
  async syncChatData(userId, chatData) {
    try {
      const chatRef = doc(db, 'user_chats', `${userId}_${chatData.chatId}`);
      await setDoc(chatRef, {
        ...chatData,
        userId,
        lastSynced: serverTimestamp(),
        updatedAt: serverTimestamp()
      }, { merge: true });
      
      return { success: true };
    } catch (error) {
      console.error('Error syncing chat data:', error);
      return { success: false, error: error.message };
    }
  }

  // ===== CLIENT DATA SYNC =====
  
  // Sync client data between bookings and chats
  async syncClientData() {
    try {
      console.log('🔄 Starting client data synchronization...');
      
      // Get all bookings
      const bookingsRef = collection(db, 'bookings');
      const bookingsSnapshot = await getDocs(bookingsRef);
      
      const syncPromises = [];
      
      bookingsSnapshot.forEach((bookingDoc) => {
        const bookingData = bookingDoc.data();
        const bookingId = bookingDoc.id;
        
        // Check if we have clientId in the booking
        if (bookingData.clientId) {
          console.log(`🔍 Syncing client data for booking ${bookingId} with clientId: ${bookingData.clientId}`);
          
          // Find corresponding chat
          const syncPromise = this.syncBookingWithChat(bookingId, bookingData);
          syncPromises.push(syncPromise);
        }
      });
      
      // Wait for all sync operations to complete
      const results = await Promise.allSettled(syncPromises);
      
      console.log('📊 Sync results:', results);
      
      return {
        success: true,
        message: 'Client data synchronization completed',
        results: results
      };
      
    } catch (error) {
      console.error('❌ Error during client data sync:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
  
  // Sync a specific booking with its corresponding chat
  async syncBookingWithChat(bookingId, bookingData) {
    try {
      console.log(`🔄 Syncing booking ${bookingId} with chat...`);
      
      // Find the chat that corresponds to this booking
      const chatsRef = collection(db, 'chats');
      const chatQuery = query(
        chatsRef,
        where('bookingId', '==', bookingId)
      );
      const chatSnapshot = await getDocs(chatQuery);
      
      if (!chatSnapshot.empty) {
        const chatDoc = chatSnapshot.docs[0];
        const chatData = chatDoc.data();
        const chatId = chatDoc.id;
        
        console.log(`✅ Found chat ${chatId} for booking ${bookingId}`);
        
        // Get client profile from users collection
        const clientProfile = await this.getClientProfile(bookingData.clientId);
        
        if (clientProfile) {
          // Update chat with correct client data
          const chatUpdates = {
            clientId: bookingData.clientId,
            clientName: clientProfile.full_name || clientProfile.displayName || clientProfile.name || 'Client',
            clientPhoto: clientProfile.profile_picture || clientProfile.profilePic || '/default-avatar.svg',
            clientEmail: clientProfile.email || '',
            updatedAt: new Date()
          };
          
          await updateDoc(doc(db, 'chats', chatId), chatUpdates);
          console.log(`✅ Updated chat ${chatId} with client data:`, chatUpdates);
          
          return {
            success: true,
            chatId: chatId,
            bookingId: bookingId,
            updates: chatUpdates
          };
        } else {
          console.log(`❌ Client profile not found for ID: ${bookingData.clientId}`);
          return {
            success: false,
            error: 'Client profile not found',
            clientId: bookingData.clientId
          };
        }
      } else {
        console.log(`❌ No chat found for booking ${bookingId}`);
        return {
          success: false,
          error: 'No chat found for booking',
          bookingId: bookingId
        };
      }
      
    } catch (error) {
      console.error(`❌ Error syncing booking ${bookingId}:`, error);
      return {
        success: false,
        error: error.message,
        bookingId: bookingId
      };
    }
  }
  
  // Get client profile from users collection
  async getClientProfile(clientId) {
    try {
      const userDoc = await getDoc(doc(db, 'users', clientId));
      if (userDoc.exists()) {
        return userDoc.data();
      }
      return null;
    } catch (error) {
      console.error(`❌ Error getting client profile for ${clientId}:`, error);
      return null;
    }
  }
  
  // Fix inconsistent client IDs between bookings and chats
  async fixInconsistentClientIds() {
    try {
      console.log('🔧 Starting to fix inconsistent client IDs...');
      
      // Get all chats
      const chatsRef = collection(db, 'chats');
      const chatsSnapshot = await getDocs(chatsRef);
      
      const fixPromises = [];
      
      chatsSnapshot.forEach((chatDoc) => {
        const chatData = chatDoc.data();
        const chatId = chatDoc.id;
        
        // Check if chat has bookingId
        if (chatData.bookingId) {
          console.log(`🔍 Checking chat ${chatId} with bookingId: ${chatData.bookingId}`);
          
          // Find the corresponding booking
          const fixPromise = this.fixChatClientId(chatId, chatData);
          fixPromises.push(fixPromise);
        }
      });
      
      // Wait for all fix operations to complete
      const results = await Promise.allSettled(fixPromises);
      
      console.log('📊 Fix results:', results);
      
      return {
        success: true,
        message: 'Client ID inconsistencies fixed',
        results: results
      };
      
    } catch (error) {
      console.error('❌ Error fixing inconsistent client IDs:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
  
  // Fix client ID for a specific chat
  async fixChatClientId(chatId, chatData) {
    try {
      console.log(`🔧 Fixing client ID for chat ${chatId}...`);
      
      // Get the booking data
      const bookingDoc = await getDoc(doc(db, 'bookings', chatData.bookingId));
      
      if (bookingDoc.exists()) {
        const bookingData = bookingDoc.data();
        
        // Check if the chat's clientId matches the booking's clientId
        if (chatData.clientId !== bookingData.clientId) {
          console.log(`⚠️ Client ID mismatch detected:`);
          console.log(`  Chat clientId: ${chatData.clientId}`);
          console.log(`  Booking clientId: ${bookingData.clientId}`);
          
          // Get client profile from the correct clientId
          const clientProfile = await this.getClientProfile(bookingData.clientId);
          
          if (clientProfile) {
            // Update chat with correct client data
            const chatUpdates = {
              clientId: bookingData.clientId,
              clientName: clientProfile.full_name || clientProfile.displayName || clientProfile.name || 'Client',
              clientPhoto: clientProfile.profile_picture || clientProfile.profilePic || '/default-avatar.svg',
              clientEmail: clientProfile.email || '',
              updatedAt: new Date()
            };
            
            await updateDoc(doc(db, 'chats', chatId), chatUpdates);
            console.log(`✅ Fixed chat ${chatId} client ID:`, chatUpdates);
            
            return {
              success: true,
              chatId: chatId,
              oldClientId: chatData.clientId,
              newClientId: bookingData.clientId,
              updates: chatUpdates
            };
          } else {
            console.log(`❌ Client profile not found for ID: ${bookingData.clientId}`);
            return {
              success: false,
              error: 'Client profile not found',
              clientId: bookingData.clientId
            };
          }
        } else {
          console.log(`✅ Chat ${chatId} client ID is consistent with booking`);
          return {
            success: true,
            message: 'No fix needed',
            chatId: chatId
          };
        }
      } else {
        console.log(`❌ Booking not found for chat ${chatId}: ${chatData.bookingId}`);
        return {
          success: false,
          error: 'Booking not found',
          bookingId: chatData.bookingId
        };
      }
      
    } catch (error) {
      console.error(`❌ Error fixing chat ${chatId}:`, error);
      return {
        success: false,
        error: error.message,
        chatId: chatId
      };
    }
  }
  
  // Real-time sync for new bookings and chats
  setupRealTimeSync() {
    try {
      console.log('🔗 Setting up real-time data sync...');
      
      // Listen for new bookings
      const bookingsRef = collection(db, 'bookings');
      const bookingsQuery = query(bookingsRef, orderBy('createdAt', 'desc'));
      
      const unsubscribeBookings = onSnapshot(bookingsQuery, async (snapshot) => {
        snapshot.docChanges().forEach(async (change) => {
          if (change.type === 'added') {
            const bookingData = change.doc.data();
            const bookingId = change.doc.id;
            
            console.log(`🆕 New booking detected: ${bookingId}`);
            
            // Sync this new booking with any existing chats
            await this.syncBookingWithChat(bookingId, bookingData);
          }
        });
      });
      
      // Listen for new chats
      const chatsRef = collection(db, 'chats');
      const chatsQuery = query(chatsRef, orderBy('createdAt', 'desc'));
      
      const unsubscribeChats = onSnapshot(chatsQuery, async (snapshot) => {
        snapshot.docChanges().forEach(async (change) => {
          if (change.type === 'added') {
            const chatData = change.doc.data();
            const chatId = change.doc.id;
            
            console.log(`🆕 New chat detected: ${chatId}`);
            
            // If this chat has a bookingId, sync it
            if (chatData.bookingId) {
              await this.fixChatClientId(chatId, chatData);
            }
          }
        });
      });
      
      return {
        unsubscribeBookings,
        unsubscribeChats
      };
      
    } catch (error) {
      console.error('❌ Error setting up real-time sync:', error);
      return null;
    }
  }

  // NEW: Enhanced real-time sync specifically for client bookings
  setupClientBookingsRealTimeSync(clientId, callback) {
    try {
      console.log('🔗 Setting up real-time client bookings sync for:', clientId);
      
      // Listen for client's bookings in real-time
      const bookingsRef = collection(db, 'bookings');
      const clientBookingsQuery = query(
        bookingsRef,
        where('clientId', '==', clientId),
        orderBy('createdAt', 'desc')
      );
      
      const unsubscribe = onSnapshot(clientBookingsQuery, async (snapshot) => {
        console.log('🔄 Real-time client bookings update detected');
        
        const updatedBookings = [];
        
        // Process each booking and fetch lawyer details
        for (const docSnapshot of snapshot.docs) {
          const bookingData = { id: docSnapshot.id, ...docSnapshot.data() };
          
          // Fetch lawyer details if lawyerId exists
          if (bookingData.lawyerId) {
            try {
              const lawyerDoc = await getDoc(doc(db, 'lawyers', bookingData.lawyerId));
              if (lawyerDoc.exists()) {
                const lawyerData = lawyerDoc.data();
                
                // Enhance booking data with lawyer details
                bookingData.lawyerName = lawyerData.full_name || lawyerData.displayName || lawyerData.name || 'Lawyer';
                bookingData.specialization = lawyerData.specialization?.join(', ') || lawyerData.specialization || 'General Law';
                bookingData.location = lawyerData.city || lawyerData.location || lawyerData.bar_council_state || 'Location not specified';
                bookingData.rating = lawyerData.average_rating || lawyerData.rating || 0;
                bookingData.experience = lawyerData.experience_years || lawyerData.experience || 0;
                bookingData.profilePic = lawyerData.profile_picture || lawyerData.profilePic || lawyerData.photoURL || lawyerData.profile_pic || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face';
                bookingData.lawyerEmail = lawyerData.email || '';
                
                // Add BAR License information
                bookingData.barLicense = lawyerData.bar_council_number || lawyerData.barCouncilLicense || lawyerData.barLicense || lawyerData.license_number || 'Not specified';
                
                // Add Chamber Address
                bookingData.chamberAddress = lawyerData.chamber_address || lawyerData.chamberAddress || lawyerData.address || lawyerData.office_address || 'Not specified';
                
                // Add Consultation Fees
                bookingData.consultationFees = lawyerData.fees?.consultation || lawyerData.consultation_fee || lawyerData.consultationFee || lawyerData.fee || 2500;
                
                // Add other fee types
                bookingData.textFee = lawyerData.fees?.text || lawyerData.text_fee || 1500;
                bookingData.callFee = lawyerData.fees?.call || lawyerData.call_fee || 2500;
                bookingData.videoFee = lawyerData.fees?.video || lawyerData.video_fee || 3000;
                bookingData.inPersonFee = lawyerData.fees?.in_person || lawyerData.in_person_fee || 3500;
                bookingData.emergencyFee = lawyerData.fees?.emergency || lawyerData.emergency_fee || 5000;
                
                // Fix consultation type mapping
                if (bookingData.consultationType === 'general') {
                  bookingData.consultationType = bookingData.type || bookingData.consultation_type || 'Video Call';
                }
                
                // Also set the type field for compatibility
                bookingData.type = bookingData.consultationType;
                
                // Add lawyer object for compatibility
                bookingData.lawyer = {
                  id: bookingData.lawyerId,
                  name: bookingData.lawyerName,
                  specialization: bookingData.specialization,
                  location: bookingData.location,
                  rating: bookingData.rating,
                  experience: bookingData.experience,
                  profilePic: bookingData.profilePic,
                  email: bookingData.lawyerEmail,
                  barLicense: bookingData.barLicense,
                  chamberAddress: bookingData.chamberAddress,
                  consultationFees: bookingData.consultationFees,
                  fees: {
                    text: bookingData.textFee,
                    call: bookingData.callFee,
                    video: bookingData.videoFee,
                    in_person: bookingData.inPersonFee,
                    emergency: bookingData.emergencyFee,
                    consultation: bookingData.consultationFees
                  }
                };
              }
            } catch (lawyerError) {
              console.error('Error fetching lawyer details for booking:', bookingData.id, lawyerError);
              // Keep the booking data even if lawyer details fail to load
            }
          }
          
          updatedBookings.push(bookingData);
        }
        
        console.log('✅ Real-time bookings update processed:', updatedBookings.length, 'bookings');
        
        // Call the callback with updated bookings
        if (callback && typeof callback === 'function') {
          callback(updatedBookings);
        }
        
        // Also update localStorage for offline support
        const userBookingsKey = `userBookings_${clientId}`;
        localStorage.setItem(userBookingsKey, JSON.stringify(updatedBookings));
        
        // Dispatch custom event for other components
        window.dispatchEvent(new CustomEvent('bookingsUpdated', { 
          detail: { bookings: updatedBookings, userId: clientId } 
        }));
      });
      
      return unsubscribe;
      
    } catch (error) {
      console.error('❌ Error setting up client bookings real-time sync:', error);
      return null;
    }
  }

  // NEW: Sync client bookings to localStorage and Firestore
  async syncClientBookings(clientId) {
    try {
      console.log('🔄 Syncing client bookings for:', clientId);
      
      // Get bookings from Firestore
      const bookingsRef = collection(db, 'bookings');
      const clientBookingsQuery = query(
        bookingsRef,
        where('clientId', '==', clientId),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(clientBookingsQuery);
      const firestoreBookings = [];
      
      // Process each booking and fetch lawyer details
      for (const docSnapshot of querySnapshot.docs) {
        const bookingData = { id: docSnapshot.id, ...docSnapshot.data() };
        
        // Fetch lawyer details if lawyerId exists
        if (bookingData.lawyerId) {
          try {
            const lawyerDoc = await getDoc(doc(db, 'lawyers', bookingData.lawyerId));
            if (lawyerDoc.exists()) {
              const lawyerData = lawyerDoc.data();
              
              // Enhance booking data with lawyer details
              bookingData.lawyerName = lawyerData.full_name || lawyerData.displayName || lawyerData.name || 'Lawyer';
              bookingData.specialization = lawyerData.specialization?.join(', ') || lawyerData.specialization || 'General Law';
              bookingData.location = lawyerData.city || lawyerData.location || lawyerData.bar_council_state || 'Location not specified';
              bookingData.rating = lawyerData.average_rating || lawyerData.rating || 0;
              bookingData.experience = lawyerData.experience_years || lawyerData.experience || 0;
              bookingData.profilePic = lawyerData.profile_picture || lawyerData.profilePic || lawyerData.photoURL || lawyerData.profile_pic || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face';
              bookingData.lawyerEmail = lawyerData.email || '';
              
              // Add BAR License information
              bookingData.barLicense = lawyerData.bar_council_number || lawyerData.barCouncilLicense || lawyerData.barLicense || lawyerData.license_number || 'Not specified';
              
              // Add Chamber Address
              bookingData.chamberAddress = lawyerData.chamber_address || lawyerData.chamberAddress || lawyerData.address || lawyerData.office_address || 'Not specified';
              
              // Add Consultation Fees
              bookingData.consultationFees = lawyerData.fees?.consultation || lawyerData.consultation_fee || lawyerData.consultationFee || lawyerData.fee || 2500;
              
              // Add other fee types
              bookingData.textFee = lawyerData.fees?.text || lawyerData.text_fee || 1500;
              bookingData.callFee = lawyerData.fees?.call || lawyerData.call_fee || 2500;
              bookingData.videoFee = lawyerData.fees?.video || lawyerData.video_fee || 3000;
              bookingData.inPersonFee = lawyerData.fees?.in_person || lawyerData.in_person_fee || 3500;
              bookingData.emergencyFee = lawyerData.fees?.emergency || lawyerData.emergency_fee || 5000;
              
              // Fix consultation type mapping
              if (bookingData.consultationType === 'general') {
                bookingData.consultationType = bookingData.type || bookingData.consultation_type || 'Video Call';
              }
              
              // Also set the type field for compatibility
              bookingData.type = bookingData.consultationType;
              
              // Add lawyer object for compatibility
              bookingData.lawyer = {
                id: bookingData.lawyerId,
                name: bookingData.lawyerName,
                specialization: bookingData.specialization,
                location: bookingData.location,
                rating: bookingData.rating,
                experience: bookingData.experience,
                profilePic: bookingData.profilePic,
                email: bookingData.lawyerEmail,
                barLicense: bookingData.barLicense,
                chamberAddress: bookingData.chamberAddress,
                consultationFees: bookingData.consultationFees,
                fees: {
                  text: bookingData.textFee,
                  call: bookingData.callFee,
                  video: bookingData.videoFee,
                  in_person: bookingData.inPersonFee,
                  emergency: bookingData.emergencyFee,
                  consultation: bookingData.consultationFees
                }
              };
            }
          } catch (lawyerError) {
            console.error('Error fetching lawyer details for booking:', bookingData.id, lawyerError);
            // Keep the booking data even if lawyer details fail to load
          }
        }
        
        firestoreBookings.push(bookingData);
      }
      
      // Also check for any existing localStorage data to merge
      const userBookingsKey = `userBookings_${clientId}`;
      const generalBookingsKey = `bookings_${clientId}`;
      const oldUserBookings = JSON.parse(localStorage.getItem('userBookings') || '[]');
      const oldGeneralBookings = JSON.parse(localStorage.getItem('bookings') || '[]');
      
      // Merge all sources
      let allBookings = [...firestoreBookings];
      
      // Add localStorage bookings that aren't in Firestore
      const allLocalBookings = [...oldUserBookings, ...oldGeneralBookings];
      allLocalBookings.forEach(localBooking => {
        const exists = allBookings.find(b => b.id === localBooking.id);
        if (!exists) {
          allBookings.push(localBooking);
        }
      });
      
      // Update localStorage with multiple keys for better persistence
      localStorage.setItem(userBookingsKey, JSON.stringify(allBookings));
      localStorage.setItem(generalBookingsKey, JSON.stringify(allBookings));
      
      // Also save to additional keys for better persistence
      const additionalKey = `clientBookings_${clientId}`;
      localStorage.setItem(additionalKey, JSON.stringify(allBookings));
      
      console.log('✅ Client bookings synced:', allBookings.length, 'bookings');
      
      return {
        success: true,
        data: allBookings,
        message: `Synced ${allBookings.length} bookings for client ${clientId}`
      };
      
    } catch (error) {
      console.error('❌ Error syncing client bookings:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Get all user chats from database
  async getUserChats(userId) {
    try {
      const chatsRef = collection(db, 'user_chats');
      const q = query(
        chatsRef,
        where('userId', '==', userId),
        orderBy('lastMessageTime', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const chats = [];
      
      querySnapshot.forEach((doc) => {
        chats.push({ id: doc.id, ...doc.data() });
      });
      
      return { success: true, data: chats };
    } catch (error) {
      console.error('Error getting user chats:', error);
      return { success: false, error: error.message };
    }
  }

  // ===== WALLET DATA SYNC =====
  
  // Sync wallet data
  async syncWalletData(userId, walletData) {
    try {
      const walletRef = doc(db, 'user_wallets', userId);
      await setDoc(walletRef, {
        ...walletData,
        userId,
        lastSynced: serverTimestamp(),
        updatedAt: serverTimestamp()
      }, { merge: true });
      
      return { success: true };
    } catch (error) {
      console.error('Error syncing wallet data:', error);
      return { success: false, error: error.message };
    }
  }

  // Get wallet data from database
  async getWalletData(userId) {
    try {
      const walletRef = doc(db, 'user_wallets', userId);
      const walletDoc = await getDoc(walletRef);
      
      if (walletDoc.exists()) {
        return { success: true, data: walletDoc.data() };
      } else {
        return { success: false, error: 'Wallet not found' };
      }
    } catch (error) {
      console.error('Error getting wallet data:', error);
      return { success: false, error: error.message };
    }
  }

  // ===== SETTINGS SYNC =====
  
  // Sync user settings
  async syncUserSettings(userId, settings) {
    try {
      const settingsRef = doc(db, 'user_settings', userId);
      await setDoc(settingsRef, {
        ...settings,
        userId,
        lastSynced: serverTimestamp(),
        updatedAt: serverTimestamp()
      }, { merge: true });
      
      return { success: true };
    } catch (error) {
      console.error('Error syncing user settings:', error);
      return { success: false, error: error.message };
    }
  }

  // Get user settings from database
  async getUserSettings(userId) {
    try {
      const settingsRef = doc(db, 'user_settings', userId);
      const settingsDoc = await getDoc(settingsRef);
      
      if (settingsDoc.exists()) {
        return { success: true, data: settingsDoc.data() };
      } else {
        return { success: false, error: 'Settings not found' };
      }
    } catch (error) {
      console.error('Error getting user settings:', error);
      return { success: false, error: error.message };
    }
  }

  // ===== COMPREHENSIVE SYNC =====
  
  // Sync all local data to database (one-time migration)
  async syncAllLocalData(userId, userRole = 'client') {
    if (this.syncInProgress) {
      console.log('Sync already in progress, queuing...');
      this.syncQueue.push({ userId, userRole });
      return { success: false, error: 'Sync already in progress' };
    }

    this.syncInProgress = true;
    
    try {
      console.log('🔄 Starting comprehensive data sync for user:', userId);
      
      const syncResults = {
        profile: false,
        chats: false,
        wallet: false,
        settings: false,
        bookings: false
      };

      // 1. Sync User Profile
      try {
        const userProfile = JSON.parse(localStorage.getItem('userProfile') || '{}');
        if (Object.keys(userProfile).length > 0) {
          const result = await this.syncUserProfile(userId, userProfile);
          syncResults.profile = result.success;
          console.log('✅ User profile sync:', result.success ? 'SUCCESS' : 'FAILED');
        }
      } catch (error) {
        console.error('❌ User profile sync error:', error);
      }

      // 2. Sync Lawyer Profile (if applicable)
      if (userRole === 'lawyer') {
        try {
          const lawyerProfile = JSON.parse(localStorage.getItem('lawyerProfile') || '{}');
          if (Object.keys(lawyerProfile).length > 0) {
            const result = await this.syncLawyerProfile(userId, lawyerProfile);
            syncResults.profile = result.success;
            console.log('✅ Lawyer profile sync:', result.success ? 'SUCCESS' : 'FAILED');
          }
        } catch (error) {
          console.error('❌ Lawyer profile sync error:', error);
        }
      }

      // 3. Sync Chat Data
      try {
        const userChats = JSON.parse(localStorage.getItem('userChats') || '[]');
        if (userChats.length > 0) {
          for (const chat of userChats) {
            await this.syncChatData(userId, chat);
          }
          syncResults.chats = true;
          console.log('✅ Chat data sync: SUCCESS');
        }
      } catch (error) {
        console.error('❌ Chat data sync error:', error);
      }

      // 4. Sync Wallet Data
      try {
        const walletBalance = localStorage.getItem('walletBalance');
        if (walletBalance) {
          const walletData = {
            balance: parseFloat(walletBalance) || 0,
            transactions: JSON.parse(localStorage.getItem('walletTransactions') || '[]')
          };
          const result = await this.syncWalletData(userId, walletData);
          syncResults.wallet = result.success;
          console.log('✅ Wallet data sync:', result.success ? 'SUCCESS' : 'FAILED');
        }
      } catch (error) {
        console.error('❌ Wallet data sync error:', error);
      }

      // 5. Sync User Settings
      try {
        const settings = {
          theme: localStorage.getItem('theme') || 'light',
          language: localStorage.getItem('language') || 'english',
          userRole: localStorage.getItem('userRole') || 'client',
          isProfileComplete: localStorage.getItem('isProfileComplete') === 'true'
        };
        const result = await this.syncUserSettings(userId, settings);
        syncResults.settings = result.success;
        console.log('✅ Settings sync:', result.success ? 'SUCCESS' : 'FAILED');
      } catch (error) {
        console.error('❌ Settings sync error:', error);
      }

      // 6. Sync Bookings (already handled by bookingService, but ensure sync)
      try {
        const userBookingsKey = `userBookings_${userId}`;
        const generalBookingsKey = `bookings_${userId}`;
        
        const userBookings = JSON.parse(localStorage.getItem(userBookingsKey) || '[]');
        const generalBookings = JSON.parse(localStorage.getItem(generalBookingsKey) || '[]');
        
        if (userBookings.length > 0 || generalBookings.length > 0) {
          syncResults.bookings = true;
          console.log('✅ Bookings sync: ALREADY HANDLED BY BOOKING SERVICE');
        }
      } catch (error) {
        console.error('❌ Bookings sync error:', error);
      }

      console.log('🎉 Comprehensive sync completed:', syncResults);
      
      // Process queued syncs
      if (this.syncQueue.length > 0) {
        const nextSync = this.syncQueue.shift();
        this.syncInProgress = false;
        await this.syncAllLocalData(nextSync.userId, nextSync.userRole);
      } else {
        this.syncInProgress = false;
      }

      return { success: true, results: syncResults };
      
    } catch (error) {
      console.error('❌ Comprehensive sync error:', error);
      this.syncInProgress = false;
      return { success: false, error: error.message };
    }
  }

  // Load all data from database to local state
  async loadAllDataFromDatabase(userId, userRole = 'client') {
    try {
      console.log('🔄 Loading all data from database for user:', userId);
      
      const loadResults = {
        profile: null,
        chats: [],
        wallet: null,
        settings: null
      };

      // 1. Load User Profile
      try {
        const profileResult = await this.getUserProfile(userId);
        if (profileResult.success) {
          loadResults.profile = profileResult.data;
          localStorage.setItem('userProfile', JSON.stringify(profileResult.data));
          console.log('✅ User profile loaded');
        }
      } catch (error) {
        console.error('❌ User profile load error:', error);
      }

      // 2. Load Lawyer Profile (if applicable)
      if (userRole === 'lawyer') {
        try {
          const lawyerProfileResult = await this.getLawyerProfile(userId);
          if (lawyerProfileResult.success) {
            loadResults.profile = lawyerProfileResult.data;
            localStorage.setItem('lawyerProfile', JSON.stringify(lawyerProfileResult.data));
            console.log('✅ Lawyer profile loaded');
          }
        } catch (error) {
          console.error('❌ Lawyer profile load error:', error);
        }
      }

      // 3. Load Chat Data
      try {
        const chatsResult = await this.getUserChats(userId);
        if (chatsResult.success) {
          loadResults.chats = chatsResult.data;
          localStorage.setItem('userChats', JSON.stringify(chatsResult.data));
          console.log('✅ Chat data loaded:', chatsResult.data.length, 'chats');
        }
      } catch (error) {
        console.error('❌ Chat data load error:', error);
      }

      // 4. Load Wallet Data
      try {
        const walletResult = await this.getWalletData(userId);
        if (walletResult.success) {
          loadResults.wallet = walletResult.data;
          localStorage.setItem('walletBalance', walletResult.data.balance.toString());
          localStorage.setItem('walletTransactions', JSON.stringify(walletResult.data.transactions || []));
          console.log('✅ Wallet data loaded');
        }
      } catch (error) {
        console.error('❌ Wallet data load error:', error);
      }

      // 5. Load User Settings
      try {
        const settingsResult = await this.getUserSettings(userId);
        if (settingsResult.success) {
          loadResults.settings = settingsResult.data;
          
          // Apply settings to localStorage
          if (settingsResult.data.theme) localStorage.setItem('theme', settingsResult.data.theme);
          if (settingsResult.data.language) localStorage.setItem('language', settingsResult.data.language);
          if (settingsResult.data.userRole) localStorage.setItem('userRole', settingsResult.data.userRole);
          if (settingsResult.data.isProfileComplete !== undefined) {
            localStorage.setItem('isProfileComplete', settingsResult.data.isProfileComplete.toString());
          }
          
          console.log('✅ Settings loaded');
        }
      } catch (error) {
        console.error('❌ Settings load error:', error);
      }

      console.log('🎉 All data loaded from database:', loadResults);
      return { success: true, data: loadResults };
      
    } catch (error) {
      console.error('❌ Load all data error:', error);
      return { success: false, error: error.message };
    }
  }

  // Real-time sync subscription
  subscribeToUserData(userId, callback) {
    try {
      const userRef = doc(db, 'user_profiles', userId);
      const unsubscribe = onSnapshot(userRef, (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          callback({ type: 'profile', data });
        }
      });

      return unsubscribe;
    } catch (error) {
      console.error('Error setting up real-time sync:', error);
      return null;
    }
  }

  // Clear local data (for logout)
  clearLocalData() {
    try {
      // Keep essential settings
      const theme = localStorage.getItem('theme');
      const language = localStorage.getItem('language');
      
      // Clear all data
      localStorage.clear();
      
      // Restore essential settings
      if (theme) localStorage.setItem('theme', theme);
      if (language) localStorage.setItem('language', language);
      
      console.log('✅ Local data cleared');
      return { success: true };
    } catch (error) {
      console.error('❌ Error clearing local data:', error);
      return { success: false, error: error.message };
    }
  }
}

// Create singleton instance
const dataSyncService = new DataSyncService();

export default dataSyncService;
